import { useState, useEffect, useMemo } from "react";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  addMonths,
  subMonths,
  isSameMonth,
  isSameDay,
  startOfDay,
  addMinutes,
  setHours,
  setMinutes,
  startOfWeek,
  endOfWeek,
  isToday,
  parse,
  addDays,
} from "date-fns";
import { formatInTimeZone, toZonedTime } from "date-fns-tz";
import { CalendarProps, BookedSlot } from "@/types/product";
import axios from "axios";
import { FaCircle } from "react-icons/fa";

type TimeRange = {
  start_time: string;
  end_time: string;
};

type DayAvailability = {
  [key: string]: TimeRange[];
};

const convertToLocalTimezone = (
  branchAvailability: DayAvailability,
  branchTimezone: string,
): DayAvailability => {
  const result: DayAvailability = {};

  const now = new Date();
  const branchDate = toZonedTime(now, branchTimezone);
  const localDate = new Date();

  const diffMinutes =
    (localDate.getTime() - branchDate.getTime()) / (1000 * 60);

  const addTimeDiff = (
    timeStr: string,
  ): { time: string; dayOffset: number } => {
    const [hours, minutes] = timeStr.split(":").map(Number);
    let totalMinutes = hours * 60 + minutes + diffMinutes;

    const dayOffset = Math.floor(totalMinutes / (24 * 60));
    totalMinutes = totalMinutes % (24 * 60);
    if (totalMinutes < 0) {
      totalMinutes += 24 * 60;
    }

    const newHours = Math.floor(totalMinutes / 60);
    const newMinutes = Math.floor(totalMinutes % 60);

    return {
      time: `${String(newHours).padStart(2, "0")}:${String(newMinutes).padStart(2, "0")}`,
      dayOffset,
    };
  };

  const daysOfWeek = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  Object.entries(branchAvailability).forEach(([day, timeRanges]) => {
    timeRanges.forEach((range) => {
      const startResult = addTimeDiff(range.start_time);
      const endResult = addTimeDiff(range.end_time);

      const dayIndex = daysOfWeek.indexOf(day);
      const startDayIndex = (dayIndex + startResult.dayOffset + 7) % 7;
      const endDayIndex = (dayIndex + endResult.dayOffset + 7) % 7;

      const startDay = daysOfWeek[startDayIndex];
      const endDay = daysOfWeek[endDayIndex];

      if (!result[startDay]) result[startDay] = [];
      if (startDay !== endDay && !result[endDay]) result[endDay] = [];

      if (startDay === endDay) {
        result[startDay].push({
          start_time: startResult.time,
          end_time: endResult.time,
        });
      } else {
        result[startDay].push({
          start_time: startResult.time,
          end_time: "24:00",
        });
        result[endDay].push({
          start_time: "00:00",
          end_time: endResult.time,
        });
      }
    });
  });

  Object.keys(result).forEach((day) => {
    result[day].sort((a, b) => a.start_time.localeCompare(b.start_time));

    const mergedSlots: TimeRange[] = [];
    let currentSlot: TimeRange | null = null;

    result[day].forEach((slot) => {
      if (!currentSlot) {
        currentSlot = { ...slot };
      } else {
        const currentEndMinutes =
          parseInt(currentSlot.end_time.split(":")[0]) * 60 +
          parseInt(currentSlot.end_time.split(":")[1]);
        const nextStartMinutes =
          parseInt(slot.start_time.split(":")[0]) * 60 +
          parseInt(slot.start_time.split(":")[1]);

        if (nextStartMinutes - currentEndMinutes <= 1) {
          currentSlot.end_time =
            slot.end_time > currentSlot.end_time
              ? slot.end_time
              : currentSlot.end_time;
        } else {
          mergedSlots.push(currentSlot);
          currentSlot = { ...slot };
        }
      }
    });

    if (currentSlot) {
      mergedSlots.push(currentSlot);
    }

    result[day] = mergedSlots;
  });
  return result;
};

export const Calendar: React.FC<CalendarProps> = ({
  employeeAvailabilityHours,
  appointmentDuration,
  setSelectedDateTime,
  selectedServiceProvider,
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [bookedSlots, setBookedSlots] = useState<BookedSlot[]>([]);
  const [localTimezone, setLocalTimezone] = useState<string>(() => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    localStorage.setItem("localTimezone", timezone);
    return timezone;
  });

  const selectedBranch = JSON.parse(
    localStorage.getItem("selectedBranch") || "{}",
  );
  const branchTimezone = selectedBranch?.timezone || "America/Chicago";

  const localAvailabilityHours = employeeAvailabilityHours
    ? convertToLocalTimezone(employeeAvailabilityHours, branchTimezone)
    : null;

  const fetchBookedSlots = async () => {
    if (!selectedServiceProvider) return;

    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_BASE_URL}api/v1/appointment/booked_slots/`,
        {
          params: {
            employee_id: selectedServiceProvider.id,
            start_date: format(new Date(), "yyyy-MM-dd"),
          },
        },
      );

      const convertedBookedSlots = response.data.map((slot: BookedSlot) => {
        const localDate = toZonedTime(
          new Date(slot.appointment_time),
          localTimezone,
        );
        return {
          ...slot,
          appointment_time: formatInTimeZone(
            localDate,
            localTimezone,
            "yyyy-MM-dd'T'HH:mm:ss.SSSxxx",
          ),
        };
      });

      setBookedSlots(convertedBookedSlots);
    } catch (error) {
      console.error("Error fetching booked slots:", error);
    }
  };

  useEffect(() => {
    fetchBookedSlots();
  }, [selectedServiceProvider, currentDate]);

  const goToPreviousMonth = () => setCurrentDate(subMonths(currentDate, 1));
  const goToNextMonth = () => setCurrentDate(addMonths(currentDate, 1));

  const calendarDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(currentDate));
    const end = endOfWeek(endOfMonth(currentDate));
    return eachDayOfInterval({ start, end });
  }, [currentDate]);

  const hasAvailableSlots = (date: Date): boolean => {
    if (date < startOfDay(new Date()) && !isToday(date)) {
      return false;
    }

    const dayOfWeek = format(date, "EEEE");
    if (!localAvailabilityHours?.[dayOfWeek]) {
      return false;
    }

    return true;
  };

  const getAvailableTimeSlots = (date: Date): string[] => {
    const selectedBranch = JSON.parse(
      localStorage.getItem("selectedBranch") || "{}",
    );
    const slots: string[] = [];
    const dayOfWeek = format(date, "EEEE");
    const daySlots = localAvailabilityHours?.[dayOfWeek] || [];
    const now = new Date();
    const appointmentBufferTime = selectedBranch?.appointment_buffer_time || 0;
    const nowWithBuffer = addMinutes(now, appointmentBufferTime);

    daySlots.forEach((slot) => {
      const [startHour, startMinute] = slot.start_time.split(":").map(Number);
      let currentTime = setMinutes(setHours(date, startHour), startMinute);

      const [endHour, endMinute] =
        slot.end_time === "24:00"
          ? [24, 0]
          : slot.end_time.split(":").map(Number);

      const endTime =
        endHour === 24
          ? addDays(setMinutes(setHours(date, 0), 0), 1)
          : setMinutes(setHours(date, endHour), endMinute);

      const lastPossibleStart = addMinutes(endTime, -appointmentDuration);

      const minutesToAdd = startMinute % 15 === 0 ? 0 : 15 - (startMinute % 15);

      currentTime = addMinutes(currentTime, minutesToAdd);

      while (currentTime <= lastPossibleStart) {
        if (isToday(date) && currentTime < nowWithBuffer) {
          currentTime = addMinutes(currentTime, 15);
          continue;
        }

        const isBooked = bookedSlots.some((bookedSlot) => {
          const bookedTime = new Date(bookedSlot.appointment_time);
          const bookedStart = new Date(bookedTime);
          const slotStart = new Date(currentTime);
          const slotEnd = addMinutes(slotStart, appointmentDuration);

          return (
            bookedSlot.employee_id === selectedServiceProvider?.id &&
            isSameDay(currentTime, bookedTime) &&
            ((slotStart >= bookedStart &&
              slotStart < addMinutes(bookedStart, bookedSlot.duration - 1)) ||
              (slotEnd > addMinutes(bookedStart, 1) &&
                slotEnd <= addMinutes(bookedStart, bookedSlot.duration)) ||
              (slotStart <= bookedStart &&
                slotEnd >= addMinutes(bookedStart, bookedSlot.duration)))
          );
        });

        if (!isBooked && currentTime.getMinutes() % 15 === 0) {
          slots.push(format(currentTime, "HH:mm"));
        }

        currentTime = addMinutes(currentTime, 15);
      }
    });

    return slots.sort();
  };

  const handleDateClick = (date: Date) => {
    if (hasAvailableSlots(date)) {
      setSelectedDate(date);
      setSelectedTime(null);
    }
  };

  const handleTimeClick = (time: string) => {
    setSelectedTime(time);
    if (selectedDate) {
      const [hours, minutes] = time.split(":").map(Number);
      const dateTime = new Date(selectedDate);
      dateTime.setHours(hours, minutes, 0);
      setSelectedDateTime(dateTime);
    }
  };

  return (
    <div className="relative w-full">
      <div
        className={`min-h-96 max-h-96 min-w-full max-w-full ${
          !selectedServiceProvider ? "opacity-70 cursor-not-allowed" : ""
        }`}
      >
        {/* Calendar Header */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={goToPreviousMonth}
            className="p-2 hover:bg-gray-100 rounded"
            disabled={!selectedServiceProvider}
          >
            &lt;
          </button>
          <h2 className="text-lg font-semibold">
            {format(currentDate, "MMMM yyyy")}
          </h2>
          <button
            onClick={goToNextMonth}
            className="p-2 hover:bg-gray-100 rounded"
            disabled={!selectedServiceProvider}
          >
            &gt;
          </button>
        </div>

        {!selectedServiceProvider && (
          <p className="text-sm text-red-500 opacity-100">
            Please select a service provider first
          </p>
        )}

        {/* Calendar Grid */}
        <div className="border rounded-lg overflow-hidden">
          {/* Weekday headers */}
          <div className="grid grid-cols-7 bg-gray-50">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
              <div key={day} className="p-2 text-center text-sm font-medium">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar days */}
          <div className="grid grid-cols-7">
            {calendarDays.map((date) => {
              const isAvailable = hasAvailableSlots(date);
              const isSelected = selectedDate && isSameDay(date, selectedDate);
              const isCurrentMonth = isSameMonth(date, currentDate);

              return (
                <button
                  key={date.toString()}
                  onClick={() => handleDateClick(date)}
                  disabled={!isAvailable || !selectedServiceProvider}
                  className={`
                    p-2 h-12 border-t border-r relative
                    ${!isCurrentMonth ? "text-gray-400" : ""}
                    ${isSelected ? "bg-red-50" : ""}
                    ${isAvailable ? "hover:bg-gray-50" : ""}
                    ${!selectedServiceProvider ? "cursor-not-allowed" : ""}
                  `}
                >
                  <span className={isToday(date) ? "text-red-500" : ""}>
                    {format(date, "d")}
                  </span>
                  {isAvailable && (
                    <div className="absolute bottom-1 left-1/2 -translate-x-1/2">
                      <FaCircle className="text-[6px] text-red-500" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Time Slot Selection */}
        {selectedDate && (
          <div className="absolute top-0 left-0 w-full h-full bg-white border rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                {format(selectedDate, "EEEE, MMMM d")}
              </h3>
              <button
                onClick={() => setSelectedDate(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="flex flex-col gap-2 overflow-y-auto max-h-[300px]">
              {getAvailableTimeSlots(selectedDate).map((time) => (
                <button
                  key={time}
                  onClick={() => handleTimeClick(time)}
                  className={`
                    p-2 text-sm rounded-md transition-colors
                    ${selectedTime === time ? "bg-red-500 text-white" : "bg-gray-100 hover:bg-gray-200"}
                  `}
                >
                  {format(parse(time, "HH:mm", selectedDate), "h:mm a")} (
                  {localTimezone})
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
